import {UpsellDialogEntryPoint} from '@Libraries/upsell-dialog.library';
import type {ReactElement} from 'react';
import {useContext, useEffect, useMemo, useRef, useState} from 'react';
import {SocialAccountLabel} from '@Components/social-media/components/social-account-label';
import {Icon} from '@Components/icon-v2';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {Button, IconPlacement, ButtonType, ButtonSize} from '@Components/button';
import {OverlayTrigger} from 'react-bootstrap';
import Tooltip from 'react-bootstrap/Tooltip';
import {SocialMediaWizardContainerQueriesContext} from '@Components/social-media-wizard/social-media-wizard-container-queries-context';
import {openSocialAccountsUpsellDialog} from '@Libraries/social-media-account.library';
import {doesUsableAccountNeedReconnection, getAccountDisplayName} from '@Components/social-media/account.vo';
import {useIsAccountReconnecting, useIsAnyAccountReconnecting, useReconnectAccount} from '@Components/social-media/social-media.hooks';
import {noop} from '@Utils/general.util';
import {isTouchDevice} from '@Utils/browser.util';
import {SelectableOptionRow} from '@Components/social-media-wizard/components/selectable-option-row/selectable-option-row';
import {GA4EventName, GA4EventParamName, SocialWizardFixThisType, trackPublishingGA4Events} from '@Libraries/ga-events';
import {SkeletonBrick} from '@Components/skeleton-brick';
import {SocialAccountRowMenu} from '@Components/social-media-wizard/components/social-account-list-item/components/social-account-row-menu';
import {PlanLockedUpgradePill} from '@Components/social-media/components/plan-locked-upgrade-pill/plan-locked-upgrade-pill';
import type {SocialAccountListItemProps} from './social-account-list-item.types';
import styles from './social-account-list-item.module.scss';
import {SocialMediaTestId} from '@/test-ids/social-media-test-id';

export function SocialAccountListItem({
  account,
  isSelected = false,
  isDisabled = false,
  tooltip,
  onCheck = noop,
  onUncheck = noop,
}: SocialAccountListItemProps): ReactElement {
  const [isChecked, setIsChecked] = useState(isSelected);
  const [isRowMenuOpen, setIsRowMenuOpen] = useState(false);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const {isSmallScreen, isExtraSmallScreen} = useContext(SocialMediaWizardContainerQueriesContext);
  const isLocked = account.isLockedByPlanLimit;
  const {startReconnect} = useReconnectAccount();
  const isReconnecting = useIsAccountReconnecting(account.id);
  const isAnyAccountReconnecting = useIsAnyAccountReconnecting();
  const isBlockedByOtherReconnect = isAnyAccountReconnecting && !isReconnecting;
  const needsReconnection = doesUsableAccountNeedReconnection(account);

  useEffect(() => {
    setIsChecked(isSelected);
  }, [isSelected]);

  const onAccountClicked = (): void => {
    const newIsChecked = !isChecked;

    setIsChecked(newIsChecked);

    if (newIsChecked) {
      onCheck(account.id);
    } else {
      onUncheck(account.id);
    }
  };

  const onRowClicked = (): void => {
    if (isLocked) {
      openSocialAccountsUpsellDialog(UpsellDialogEntryPoint.SOCIAL_WIZARD_ACCOUNT_LIST_ITEM);
      return;
    }

    if (isRowUnselectable()) {
      return;
    }

    onAccountClicked();
  };

  const toolTipElement = useMemo((): ReactElement | null => {
    if (!tooltip) {
      return null;
    }

    return (
      <OverlayTrigger
        trigger={['hover', 'focus', 'click']}
        overlay={
          <Tooltip
            id={`tooltip-overlay-${account.id}`}
            className={`_pointer-events-none ${isExtraSmallScreen || isSmallScreen ? styles.customTooltip : ''}`}
            style={{position: 'fixed'}}
          >
            {tooltip.text}
          </Tooltip>
        }
        delay={{show: 50, hide: 50}}
        placement="top"
        popperConfig={undefined}
        show={undefined}
        defaultShow={undefined}
        onHide={undefined}
        onToggle={undefined}
        target={undefined}
        flip={undefined}
        offset={undefined}
      >
        <Icon
          ref={tooltipRef}
          icon={tooltip.icon}
          className={`${styles.tooltipIcon} ${tooltip.classes ?? ''}`}
          size={IconSize.SIZE_ICON_20}
          type={IconType.NONE}
          shape={IconShape.NONE}
        />
      </OverlayTrigger>
    );
  }, [tooltip]);

  const getThemeClassesForRow = (): string => {
    if (needsReconnection) {
      return styles.error;
    }

    if (isDisabled) {
      return styles.disabled;
    }

    return '';
  };

  const isRowUnselectable = (): boolean => {
    return isDisabled || needsReconnection;
  };

  const reconnectAccountAndSelectIt = (): void => {
    startReconnect(account, {
      onReconnected: (accountId) => {
        onCheck(accountId);
      },
    });
  };

  const onFixThis = (): void => {
    trackPublishingGA4Events(GA4EventName.SOCIALWIZARD_FIXTHIS, {[GA4EventParamName.TYPE]: SocialWizardFixThisType.ACCOUNT_LIST});
    reconnectAccountAndSelectIt();
  };

  const onRefreshAccount = (): void => {
    startReconnect(account);
  };

  const onRowMenuOpened = (): void => {
    setIsRowMenuOpen(true);
  };

  const onRowMenuClosed = (): void => {
    setIsRowMenuOpen(false);
  };

  const getUpgradeToUsePill = (): ReactElement => {
    return (
      <PlanLockedUpgradePill
        tooltip={{
          id: `plan-limit-tooltip-${account.id}`,
          tooltipClasses: `_pointer-events-none ${isExtraSmallScreen || isSmallScreen ? styles.customTooltip : ''}`,
          style: {position: 'fixed'},
        }}
      />
    );
  };

  const getFixThisButton = (): ReactElement => {
    return (
      <Button
        type={ButtonType.SECONDARY}
        size={ButtonSize.SMALL}
        iconPlacement={IconPlacement.LEFT}
        icon="icon-refresh"
        text={window.i18next.t('pmwjs_social_fix_this')}
        onClick={onFixThis}
        disabled={isBlockedByOtherReconnect}
        customClasses={styles.fixThisButton}
      />
    );
  };

  const getActionElement = (): ReactElement | null => {
    if (isLocked) {
      return getUpgradeToUsePill();
    }

    if (needsReconnection) {
      return getFixThisButton();
    }

    if (toolTipElement) {
      return toolTipElement;
    }

    return null;
  };

  const getRowMenuVisibilityClass = (): string => {
    if (isRowMenuOpen || needsReconnection || isTouchDevice()) {
      return styles.rowMenuVisible;
    }

    return '';
  };

  const getRowMenu = (): ReactElement => {
    return (
      <SocialAccountRowMenu
        account={account}
        className={`${styles.rowMenu} ${getRowMenuVisibilityClass()}`}
        onRefreshAccount={onRefreshAccount}
        onMenuOpened={onRowMenuOpened}
        onMenuClosed={onRowMenuClosed}
      />
    );
  };

  const isDisabledState = (): boolean => {
    return isDisabled && !needsReconnection;
  };

  const getSkeletonRow = (): ReactElement => {
    return (
      <div className={styles.listItem}>
        <div className={styles.accountDetails}>
          <SkeletonBrick height={20} width={20} />
          <SkeletonBrick height={32} width={32} className="radius-round" />
          <SkeletonBrick height={16} width={140} />
        </div>
      </div>
    );
  };

  if (isReconnecting) {
    return getSkeletonRow();
  }

  return (
    <SelectableOptionRow
      data-testid={SocialMediaTestId.WIZARD_ACCOUNT_ROW}
      className={`${styles.listItem} ${getThemeClassesForRow()} ${isChecked ? styles.selected : ''}`}
      checkboxClasses={styles.accountCheckbox}
      isChecked={isChecked}
      isDisabled={isRowUnselectable() && !isLocked}
      onToggle={onRowClicked}
      checkboxAriaLabel={getAccountDisplayName(account)}
    >
      <div className={styles.accountDetails}>
        <SocialAccountLabel
          className={styles.accountLabel}
          account={account}
          isDisabled={isDisabledState()}
          isConnectionExpired={needsReconnection}
          isSelected={isChecked}
        />
      </div>
      <div className={styles.rowActions}>
        {getActionElement()}
        {getRowMenu()}
      </div>
    </SelectableOptionRow>
  );
}
